import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import moment = require('moment');
import DateSelectionCard from '../DateSelectionCard';
import renderIcon from 'helpers/renderIcon';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { BookingListItemDto } from 'models/bookappointment';

interface IProps {
	t?: (key: string) => string;
	setDates: (newDateArray: string[], daysToLoad: number, dateIndex: number) => void;
	setSelectedDate: (date: string) => void;
	roundDateTime: (time: moment.Moment, mode: string) => moment.Moment;
	dateSelectionArray: string[];
	dateSelectionArrayMobile: string[];
	dateSelectionObject: { [date: string]: BookingListItemDto[] };
	selectedDate: string;
	timezone?: string;
	isFetching: boolean;
}

interface IState {
	yCoordinate: number;
	isSecondMobileLoad: boolean;
	mobileMonthWidths: { [monthNumber: number]: number };
	currentMonth: string;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class DateSelectionContainer extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);
		this.state = {
			yCoordinate: 0,
			isSecondMobileLoad: false,
			mobileMonthWidths: null,
			currentMonth: null,
		};
		this.scrollContainerRef = React.createRef();
		this.handleScrollY = this.handleScrollY.bind(this);
		this.handleScrollX = this.handleScrollX.bind(this);
	}

	private scrollContainerRef;

	public componentDidMount() {
		window.addEventListener('scroll', this.handleScrollY);
		this.scrollContainerRef.current.addEventListener('scroll', this.handleScrollX);
		if (this?.scrollContainerRef?.current) {
			this?.scrollContainerRef?.current.scrollTo(0, 0);
		}
	}

	public componentDidUpdate(prevProps: Readonly<IProps>): void {
		if (
			(!this.state.mobileMonthWidths && this.props.dateSelectionArrayMobile.length) ||
			this.props?.dateSelectionArrayMobile.length !== prevProps?.dateSelectionArrayMobile.length
		) {
			const daysInMonthsArrayObj = {};

			this.props.dateSelectionArrayMobile.forEach((date) => {
				const monthKey = momentWithTimezone(this.props?.timezone, date)
					.format('MMMM yyyy')
					.toString();

				if (!daysInMonthsArrayObj[monthKey]) {
					const endOfMonth = momentWithTimezone(this.props?.timezone, date).endOf('month');
					const dayInMonth = momentWithTimezone(this.props?.timezone, date);

					daysInMonthsArrayObj[monthKey] = endOfMonth.diff(dayInMonth, 'days');
				}
			});

			const monthWidths = {};

			Object.keys(daysInMonthsArrayObj).forEach((key) => {
				monthWidths[key] = Math.floor(daysInMonthsArrayObj[key] * 74 + 74);
			});

			if (!this.state.mobileMonthWidths) {
				const currentMonth = Object.keys(monthWidths)[0];
				this.setState({ mobileMonthWidths: monthWidths, currentMonth });
			} else {
				this.setState({ mobileMonthWidths: monthWidths });
			}
		}
	}

	public componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScrollY);
		this.scrollContainerRef.current.removeEventListener('scroll', this.handleScrollX);
	}

	public handleScrollY = () => {
		const position = window.pageYOffset;
		this.setState({ yCoordinate: position });
	};

	public handleScrollX = () => {
		const { dateSelectionArrayMobile, dateSelectionObject } = this.props;
		const scrollX = this.scrollContainerRef.current.scrollLeft;
		let dateIndex;

		for (let el = 0; el < dateSelectionArrayMobile.length; el++) {
			const leftOffset = el * 74 - 370;

			if (scrollX >= leftOffset) {
				dateIndex = el;
			}
		}

		if (this.state.mobileMonthWidths) {
			const { mobileMonthWidths } = this.state;
			let totalWidth = 0;
			let currentMonth = null;

			for (const month in mobileMonthWidths) {
				if (Object.prototype.hasOwnProperty.call(mobileMonthWidths, month)) {
					totalWidth += mobileMonthWidths[month];
					if (scrollX < totalWidth) {
						currentMonth = month;
						break;
					}
				}
			}
			if (currentMonth !== this.state.currentMonth) {
				this.setState({ currentMonth });
			}
		}

		const dateKey = dateSelectionArrayMobile[dateIndex];

		// Fetch dates only when nearing the end of currently loaded ones
		if (!this.props.isFetching && dateKey && !(dateKey in dateSelectionObject)) {
			if (!this.state.isSecondMobileLoad) {
				this.loadNewDates('add', 7, 7, dateIndex);
				this.setState({ isSecondMobileLoad: true });
			} else {
				this.loadNewDates('add', 7, 7, dateIndex);
			}
		}
	};

	private loadNewDates = (actionToDo: string, daysToChange: number, daysToLoad: number, dateIndex?: number) => {
		const { dateSelectionArray, dateSelectionArrayMobile, setDates } = this.props;

		const selectionArray = daysToLoad === 7 ? dateSelectionArray : dateSelectionArrayMobile;

		const newDateSelectionArray = selectionArray.map((date) => {
			const timezone = this.props?.timezone;
			if (actionToDo === 'add') {
				return momentWithTimezone(timezone, date).add(daysToChange, 'days').toISOString();
			}
			if (actionToDo === 'subtract') {
				return momentWithTimezone(timezone, date).subtract(daysToChange, 'days').toISOString();
			}
		});

		setDates(newDateSelectionArray, daysToLoad, dateIndex);
	};

	render() {
		const { dateSelectionArray, dateSelectionArrayMobile, dateSelectionObject, setSelectedDate, timezone } = this.props;
		const { currentMonth } = this.state;

		const objItems = {};
		const objItemsMobile = {};

		dateSelectionArray.forEach((date) => {
			if (date in dateSelectionObject) {
				objItems[date] = [];
				dateSelectionObject[date].forEach((el) => {
					const elStartTime = momentWithTimezone(timezone, el.schedule).startOf('day').toISOString();
					if (elStartTime in objItems) {
						(objItems[elStartTime] = objItems[elStartTime] || []).push(el);
					}
				});
			}
		});

		dateSelectionArrayMobile.forEach((date) => {
			if (date in dateSelectionObject) {
				objItemsMobile[date] = [];
				dateSelectionObject[date].forEach((el) => {
					const elStartTime = momentWithTimezone(timezone, el.schedule).startOf('day').toISOString();
					if (elStartTime in objItemsMobile) {
						(objItemsMobile[elStartTime] = objItemsMobile[elStartTime] || []).push(el);
					}
				});
			}
		});

		// If before current date don't allow to go further back
		const isBeforeToday: boolean = momentWithTimezone(timezone, dateSelectionArray[0]).isBefore(
			momentWithTimezone(timezone),
		);

		return (
			<>
				<div className="dateSelectionContainer">
					<div className="dateSelectionContainer__desktopWrapper">
						<div
							className={`dateSelectionContainer__leftButton ${isBeforeToday ? 'unclickable' : ''}`}
							onClick={() => !isBeforeToday && this.loadNewDates('subtract', 7, 7)}
						>
							{renderIcon('roundLeft')}
						</div>

						<div className="dateSelectionContainer__datesDesktop">
							{dateSelectionArray.map((date, i) => {
								const showMonthAndYear = i === 0 || momentWithTimezone(timezone, date).date() === 1 ? true : false;
								const isFirstDayAfterFirstElement =
									i === 0 && momentWithTimezone(timezone, dateSelectionArray[i + 1]).date() === 1 ? true : false;
								const slots = objItems[date]?.length;
								return (
									<DateSelectionCard
										date={date}
										key={i}
										slotsAvailable={slots}
										setSelectedDate={setSelectedDate}
										timezone={timezone}
										selectedDate={this.props.selectedDate}
										showMonthAndYear={showMonthAndYear}
										isFirstDayAfterFirstElement={isFirstDayAfterFirstElement}
									/>
								);
							})}
						</div>

						<div className="dateSelectionContainer__rightButton" onClick={() => this.loadNewDates('add', 7, 7)}>
							{renderIcon('roundRight')}
						</div>
					</div>

					<div className="dateSelectionContainer__datesMobile__monthsContainer">{currentMonth}</div>

					<div className="dateSelectionContainer__datesMobile" ref={this.scrollContainerRef}>
						{dateSelectionArrayMobile.map((date, i) => {
							const showMonthAndYear =
								momentWithTimezone(timezone, date).date() === 1 &&
								momentWithTimezone(timezone, date).format('MMMM yyyy').toString() !== currentMonth
									? true
									: false;
							const isFirstDayAfterFirstElement =
								i === 0 && momentWithTimezone(timezone, dateSelectionArrayMobile[i + 1]).date() === 1 ? true : false;
							const slots = objItemsMobile[date]?.length;
							const isFirstElement = i === 0 ? true : false;
							const isLastElement = i === dateSelectionArrayMobile.length - 1 ? true : false;

							return (
								<DateSelectionCard
									date={date}
									key={i}
									slotsAvailable={slots}
									setSelectedDate={setSelectedDate}
									timezone={timezone}
									selectedDate={this.props.selectedDate}
									showMonthAndYear={showMonthAndYear}
									isFirstDayAfterFirstElement={isFirstDayAfterFirstElement}
									isFirstElement={isFirstElement}
									isLastElement={isLastElement}
								/>
							);
						})}
					</div>

					<div
						className={`dateSelectionContainer__datesMobile__borderBottom${
							this.state.yCoordinate >= 100 ? '__visible' : ''
						}`}
					></div>
					<div
						className={`dateSelectionContainer__datesDesktop__borderBottom${
							this.state.yCoordinate >= 150 ? '__visible' : ''
						}`}
					></div>
				</div>
			</>
		);
	}
}

export default DateSelectionContainer;
